import { useTranslation } from 'react-i18next';

import type { CardFilter, Label } from '@/entities/board';

import styles from './BoardFilterBar.module.css';

interface Props {
  labels: Label[];
  filter: CardFilter;
  onChange: (update: (current: CardFilter) => CardFilter) => void;
}

/** One removable chip per active filter, in a single row that scrolls inside the bar. */
export function ActiveFilterChips({ labels, filter, onChange }: Props) {
  const { t } = useTranslation();
  const chips: { key: string; name: string; remove: (c: CardFilter) => CardFilter }[] = [];
  const statusNames = { open: 'filter.statusOpen', done: 'filter.statusDone' } as const;
  for (const value of filter.status) {
    chips.push({
      key: `status-${value}`,
      name: t(statusNames[value]),
      remove: (c) => ({ ...c, status: c.status.filter((x) => x !== value) }),
    });
  }
  const deadlineNames = {
    has: 'filter.deadlineHas',
    none: 'filter.deadlineNone',
    overdue: 'filter.deadlineOverdue',
    today: 'filter.deadlineToday',
    tomorrow: 'filter.deadlineTomorrow',
  } as const;
  for (const value of filter.deadline) {
    chips.push({
      key: `deadline-${value}`,
      name: t(deadlineNames[value]),
      remove: (c) => ({ ...c, deadline: c.deadline.filter((x) => x !== value) }),
    });
  }
  const assignmentNames = {
    me: 'assignment.filterMine',
    others: 'assignment.filterOthers',
    shared: 'assignment.filterShared',
    unassigned: 'assignment.filterUnassigned',
  } as const;
  for (const value of filter.assignment) {
    chips.push({
      key: `assignment-${value}`,
      name: t(assignmentNames[value]),
      remove: (c) => ({ ...c, assignment: c.assignment.filter((x) => x !== value) }),
    });
  }
  for (const id of filter.labelIds) {
    const label = labels.find((l) => l.id === id);
    chips.push({
      key: `label-${id}`,
      name: label?.name || t('labels.unnamed'),
      remove: (c) => ({ ...c, labelIds: c.labelIds.filter((x) => x !== id) }),
    });
  }
  return (
    <ul className={styles.chips} aria-label={t('filter.active')}>
      {chips.map((chip) => (
        <li key={chip.key}>
          <button
            type="button"
            className={styles.chip}
            aria-label={t('filter.removeFilter', { name: chip.name })}
            onClick={() => {
              onChange(chip.remove);
            }}
          >
            <span className={styles.chipName}>{chip.name}</span>
            <span aria-hidden="true">×</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
